import { useEffect, useState } from "react";
import { api, isVideoPath, directFileUrl } from "@/lib/api";

// Resolve an object-storage path to a viewable URL via the admin-authed files endpoint.
export const authedResolve = async (path) => {
  const r = await api.get(`/files/${path}`, { responseType: "blob" });
  return URL.createObjectURL(r.data);
};

// Small square preview (server-cropped) for thumbnail grids — the viewer still loads full size.
export const authedThumbResolve = async (path) => {
  if (isVideoPath(path)) return authedResolve(path);
  const r = await api.get(`/files/${path}?thumb=1`, { responseType: "blob" });
  return URL.createObjectURL(r.data);
};

// Crew + admin view of a customer's problem photos (staff-scoped endpoint, not admin-only /files).
export const staffPhotoResolve = async (path) => {
  const r = await api.get(`/staff/customer-photo/${path}`, { responseType: "blob" });
  return URL.createObjectURL(r.data);
};

// Streams a video straight from the files endpoint (HTTP Range) — blob-loading a whole
// clip just to show its first frame would be slow and heavy on a phone.
export const SmartVideo = ({ path, resolve, className = "", contain = false, testid }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let live = true;
    setUrl(null);
    const promise = resolve && resolve !== authedResolve
      ? Promise.resolve().then(() => resolve(path))   // e.g. portal token URL (already direct)
      : Promise.resolve(directFileUrl(path));
    promise.then((u) => { if (live) setUrl(u); }).catch(() => {});
    return () => { live = false; };
  }, [path, resolve]);
  if (!url) return <span data-testid={testid} className={`block bg-secondary animate-pulse ${className}`} />;
  return (
    <video
      data-testid={testid}
      src={url}
      className={className}
      controls={contain}
      autoPlay={contain}
      muted={!contain}
      playsInline
      preload={contain ? "auto" : "metadata"}
      draggable={false}
    />
  );
};

// Image that loads via a resolve() function (async blob URL or direct URL string).
// Video paths route to SmartVideo (thumb = muted first frame; contain = full player).
export const SmartImage = ({ path, resolve = authedResolve, className = "", contain = false, testid }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let blobUrl;
    let live = true;
    setUrl(null);
    if (isVideoPath(path)) return; // handled by the render branch below
    Promise.resolve()
      .then(() => resolve(path))
      .then((u) => {
        if (!live) {
          if (u && u.startsWith("blob:")) URL.revokeObjectURL(u);
          return;
        }
        if (u && u.startsWith("blob:")) blobUrl = u;
        setUrl(u);
      })
      .catch(() => {});
    return () => {
      live = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [path, resolve]);
  if (isVideoPath(path)) return <SmartVideo path={path} resolve={resolve} className={className} contain={contain} testid={testid} />;
  if (!url) return <span data-testid={testid} className={`block bg-secondary animate-pulse ${className}`} />;
  return (
    <img
      data-testid={testid}
      src={url}
      alt="Job photo"
      draggable={false}
      className={`${contain ? "object-contain" : "object-cover"} ${className}`}
    />
  );
};
